import { CheckCircle2, Loader2, Send, RotateCw, AlertTriangle, Pencil } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { BrandButton } from "@/components/PageHeader";
import { fmtDate } from "@/lib/api";

export const GROUPS = [["member", "Paid members"], ["protocol", "Protocol"], ["sponsor", "Sponsors"], ["student", "Students / Interns"]];
const GROUP_LABEL = { member: "Member", protocol: "Protocol", sponsor: "Sponsor", student: "Student / Intern" };

export const canSend = (r) => r.paid_ok && r.has_email;

const CertStatus = ({ c }) => {
  if (c?.sending) { return <span className="inline-flex items-center gap-1 text-xs text-gray-600" data-testid="cd-status-sending"><Loader2 className="w-3.5 h-3.5 animate-spin" />Sending…</span>; }
  if (c?.sent_at) {
    return <div data-testid="cd-status-sent"><span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 rounded-full px-2 py-0.5"><CheckCircle2 className="w-3.5 h-3.5" />Certificate sent</span>
      <div className="text-[11px] text-gray-500 mt-0.5 font-mono">{c.serial}{c.sent_count > 1 ? ` · sent ${c.sent_count}×` : ""} · {fmtDate(c.sent_at)}</div></div>;
  }
  if (c?.error) { return <span className="inline-flex items-center gap-1 text-xs text-[#B30000]" data-testid="cd-status-error"><AlertTriangle className="w-3.5 h-3.5" />{c.error}</span>; }
  return <span className="text-xs text-gray-400">Not sent</span>;
};

const Why = ({ r }) => <span className="text-[11px] text-gray-500">{!r.paid_ok ? "Not fully paid" : "No email address"}</span>;

const payLabel = (r) => (["protocol", "sponsor"].includes(r.group) ? "Payment not required" : r.status);

const CertName = ({ r, sfx = "", onEdit }) => (
  <div data-testid={`cd-cert-name${sfx}-${r.ticket_no}`}>
    <div className="font-medium inline-flex items-center gap-1.5">{r.certificate_name}
      {onEdit && <button type="button" onClick={() => onEdit(r)} className="text-gray-400 hover:text-[#E60000]" aria-label="Correct certificate name" data-testid={`cd-edit-name${sfx}-${r.ticket_no}`}><Pencil className="w-3.5 h-3.5" /></button>}</div>
    {r.cert_name_missing && <span className="inline-flex items-center gap-1 mt-0.5 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid={`cd-cert-name-missing${sfx}-${r.ticket_no}`}><AlertTriangle className="w-3 h-3" />No certificate name given</span>}
    {r.cert?.sent_at && r.cert.name && r.cert.name !== r.certificate_name && <div className="text-[11px] text-gray-500 mt-0.5" data-testid={`cd-cert-name-old${sfx}-${r.ticket_no}`}>Sent as “{r.cert.name}”. Resend to update.</div>}
  </div>
);

const Row = ({ r, checked, onCheck, onSend, onEditName, busy }) => (
  <tr className="hover:bg-[#F8F9FA] align-top" data-testid={`cd-row-${r.ticket_no}`}>
    <td className="px-3 py-2.5"><Checkbox checked={checked} disabled={!canSend(r)} onCheckedChange={(v) => onCheck(r.id, v)} data-testid={`cd-check-${r.ticket_no}`} /></td>
    <td className="px-3 py-2.5 font-mono text-xs font-semibold text-[#E60000] whitespace-nowrap">{r.ticket_no}</td>
    <td className="px-3 py-2.5"><div className="font-medium">{r.name}</div><div className="text-xs text-gray-500">{r.category_name}</div>
      <div className="md:hidden mt-1 text-xs"><span className="text-gray-500">On certificate: </span><CertName r={r} sfx="-m" onEdit={onEditName} /></div>
      <div className="md:hidden text-[11px] text-gray-500">{GROUP_LABEL[r.group]} · {payLabel(r)}{r.checked_in ? " · Checked in" : ""}</div><div className="md:hidden mt-1"><CertStatus c={r.cert} /></div></td>
    <td className="hidden md:table-cell px-3 py-2.5 text-sm min-w-[180px]"><CertName r={r} onEdit={onEditName} /></td>
    <td className="hidden md:table-cell px-3 py-2.5 text-xs whitespace-nowrap"><div>{GROUP_LABEL[r.group]}</div><div className="text-gray-500">{payLabel(r)}{r.checked_in ? " · Checked in" : ""}</div></td>
    <td className="hidden md:table-cell px-3 py-2.5"><CertStatus c={r.cert} /></td>
    <td className="px-3 py-2.5 text-right whitespace-nowrap">
      {canSend(r) ? (
        <BrandButton variant="outline" onClick={() => onSend(r)} disabled={busy || r.cert?.sending} data-testid={`cd-send-${r.ticket_no}`}>
          {r.cert?.sent_at ? <><RotateCw className="w-4 h-4" />Resend</> : <><Send className="w-4 h-4" />Send</>}
        </BrandButton>
      ) : <Why r={r} />}
    </td>
  </tr>
);

export const DispatchTable = ({ rows, selected, onCheck, onCheckAll, onSend, onEditName, busy }) => {
  const pickable = rows.filter(canSend);
  const all = pickable.length > 0 && pickable.every((r) => selected.has(r.id));
  return (
    <table className="w-full text-sm" data-testid="cd-table">
      <thead className="bg-[#F8F9FA] text-[11px] uppercase tracking-wider text-gray-500">
        <tr>
          <th className="px-3 py-2.5 w-8"><Checkbox checked={all} disabled={!pickable.length} onCheckedChange={(v) => onCheckAll(pickable, v)} data-testid="cd-check-all" /></th>
          {["Ticket No", "Participant", "Name on certificate", "Group / Payment", "Certificate", ""].map((h, i) => <th key={i} className={`text-left font-bold px-3 py-2.5 whitespace-nowrap ${["", "", "hidden md:table-cell", "hidden md:table-cell", "hidden md:table-cell", ""][i]}`}>{h}</th>)}
        </tr>
      </thead>
      <tbody className="divide-y">{rows.map((r) => <Row key={r.id} r={r} checked={selected.has(r.id)} onCheck={onCheck} onSend={onSend} onEditName={onEditName} busy={busy} />)}</tbody>
    </table>
  );
};
